import { MapPin, Phone, Mail, MessageCircle, Train, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Reveal } from "@/components/Reveal";
import { CONTACT } from "@/content";
import { useSettings } from "@/hooks/useSettings";
import { Seo } from "@/components/Seo";

export default function Kontakt() {
  const { hours } = useSettings();
  const cards = [
    {
      icon: Phone,
      title: "Telefon",
      testid: "kontakt-phone-card",
      content: (
        <>
          <p className="text-sm text-muted-foreground">
            Der schnellste Weg zu uns – ruf an, wir beraten dich gerne.
          </p>
          <a
            href={CONTACT.phoneHref}
            data-testid="kontakt-phone-link"
            className="link-slide mt-3 inline-block text-sm font-medium text-primary"
          >
            {CONTACT.phoneDisplay} →
          </a>
        </>
      ),
    },
    {
      icon: MapPin,
      title: "Adresse",
      testid: "kontakt-address-card",
      content: (
        <>
          <p className="text-sm font-medium">{CONTACT.addressStreet}</p>
          <p className="text-sm text-muted-foreground">{CONTACT.addressCity}</p>
          <a
            href={CONTACT.mapsHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="kontakt-maps-link"
            className="link-slide mt-3 inline-block text-sm font-medium text-primary"
          >
            Route planen →
          </a>
        </>
      ),
    },
    {
      icon: MessageCircle,
      title: "WhatsApp",
      testid: "kontakt-whatsapp-card",
      content: (
        <>
          <p className="text-sm text-muted-foreground">
            Für Fragen, Ankauf &amp; Co. – auch außerhalb der Öffnungszeiten.
          </p>
          <a
            href={CONTACT.whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="kontakt-whatsapp-link"
            className="link-slide mt-3 inline-block text-sm font-medium text-primary"
          >
            Chat starten →
          </a>
        </>
      ),
    },
    {
      icon: Mail,
      title: "E-Mail",
      testid: "kontakt-email-card",
      content: (
        <>
          <p className="text-sm text-muted-foreground">
            Für alles, was etwas mehr Platz braucht.
          </p>
          <a
            href={CONTACT.emailHref}
            data-testid="kontakt-email-link"
            className="link-slide mt-3 inline-block text-sm font-medium text-primary"
          >
            {CONTACT.email} →
          </a>
        </>
      ),
    },
  ];

  return (
    <div data-testid="kontakt-page">
      <Seo
        title="Kontakt & Öffnungszeiten | Shibuya Anime & TCG Store Köln"
        description="Shibuya Anime & TCG Store Köln – Zülpicher Str. 357, 50935 Köln. Öffnungszeiten: Mo–Fr 12–20 Uhr, Sa 10–20 Uhr. Telefon 0221 96260815. Anfahrt, WhatsApp & E-Mail."
        path="/kontakt"
        keywords="Shibuya Store Köln Öffnungszeiten, Anime Store Köln Adresse, TCG Laden Köln, Zülpicher Straße Anime Shop"
      />
      {/* ============ HERO ============ */}
      <section className="hero-glow noise relative overflow-hidden border-b border-border">
        <div
          aria-hidden="true"
          className="neon-flicker pointer-events-none absolute right-8 top-1/2 z-10 hidden -translate-y-1/2 select-none lg:block"
        >
          <p className="neon-kanji kanji-vertical text-4xl">連絡先</p>
        </div>
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-6 top-0 select-none font-jp text-[140px] font-black leading-none text-white/[0.04] md:text-[220px]"
        >
          連絡
        </div>
        <div className="relative z-10 mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-24">
          <Reveal>
            <Badge
              variant="outline"
              className="mb-6 rounded-full border-primary/40 bg-primary/10 px-4 py-1.5 font-mono-ui text-[11px] uppercase tracking-widest text-primary"
            >
              Wir sind für dich da
            </Badge>
            <h1 className="max-w-3xl font-display text-5xl uppercase leading-[0.95] tracking-[0.04em] sm:text-6xl lg:text-7xl">
              Sag hallo<span className="neon-text">.</span>
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
              Ob Frage zum Sortiment, Ankauf-Anfrage oder einfach Bock auf einen
              Plausch über die neuesten Sets – melde dich, wie es dir am besten
              passt.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ============ KONTAKT CARDS ============ */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-24">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {cards.map((card, i) => (
            <Reveal key={card.title} delay={i * 0.06}>
              <div
                data-testid={card.testid}
                className="noise relative h-full overflow-hidden rounded-2xl border border-border bg-card p-6 neon-card"
              >
                <div className="relative z-10">
                  <card.icon className="h-6 w-6 text-primary" />
                  <p className="mt-4 font-display text-2xl uppercase tracking-wide">
                    {card.title}
                  </p>
                  <div className="mt-2">{card.content}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ MAP ============ */}
      <section className="border-t border-border bg-card/50">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-24">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <Reveal className="lg:col-span-4">
              <p className="font-mono-ui text-xs uppercase tracking-[0.25em] text-primary">
                Anfahrt
              </p>
              <h2 className="mt-3 font-display text-4xl uppercase tracking-[0.04em]">
                So findest du uns.
              </h2>
              <div className="mt-6 space-y-4">
                <div className="flex items-start gap-3 rounded-xl border border-border bg-card p-4">
                  <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  <div>
                    <p className="text-sm font-medium">{CONTACT.addressStreet}</p>
                    <p className="text-sm text-muted-foreground">{CONTACT.addressCity}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-xl border border-border bg-card p-4">
                  <Clock className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  <div className="w-full">
                    <p className="text-sm font-medium">Öffnungszeiten</p>
                    <div data-testid="kontakt-opening-hours" className="mt-1.5 space-y-1">
                      {hours.map((row) => (
                        <div key={row.days} className="flex justify-between gap-4 text-sm text-muted-foreground">
                          <span>{row.days}</span>
                          <span className={row.time === "Geschlossen" ? "" : "text-foreground"}>{row.time}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-xl border border-border bg-card p-4">
                  <Train className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  <div>
                    <p className="text-sm font-medium">Gut erreichbar</p>
                    <p className="text-sm text-muted-foreground">
                      Mit der KVB über die Zülpicher Straße – oder mit dem Auto:
                      Parkmöglichkeiten in der Umgebung.
                    </p>
                  </div>
                </div>
              </div>
              <Button
                asChild
                data-testid="kontakt-route-button"
                className="mt-6 h-11 rounded-xl bg-primary px-5 font-medium text-primary-foreground"
              >
                <a href={CONTACT.mapsHref} target="_blank" rel="noopener noreferrer">
                  <MapPin className="mr-2 h-4 w-4" /> In Google Maps öffnen
                </a>
              </Button>
            </Reveal>
            <Reveal className="lg:col-span-8" delay={0.1}>
              <div className="h-full min-h-[360px] overflow-hidden rounded-2xl border border-border shadow-xl shadow-black/40">
                <iframe
                  title="Shibuya Store Köln – Karte"
                  data-testid="kontakt-map-embed"
                  src={CONTACT.mapsEmbed}
                  className="h-full min-h-[360px] w-full"
                  style={{ border: 0, filter: "invert(90%) hue-rotate(180deg) saturate(0.5)" }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
